import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { trackClientEvent } from 'analytics/analytics'
import OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import { OfferListEventsProvider, type OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import { ChertoExperiments } from 'constants/experiments'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import useExperiment from 'hooks/Experiments/useExperiment'
import useOfferList from 'hooks/Offers/useOfferList'
import useFamilySearchIntent from 'home/hooks/useFamilySearchIntent'
import useRecentSearchHotelCarousel from 'home/hooks/useRecentSearchHotelCarousel'
import {
  buildFamilyFriendlyOfferFilters,
  buildSearchSlotSelectionEvent,
  selectPersonalisedCarousel,
  type PersonalisedCarouselCandidate,
  type PersonalisedCarouselSelection,
} from './homepageSearchSlotOfferCarouselUtils'
import RecentSearchHotelOfferCarousel from './RecentSearchHotelOfferCarousel'
import HomepageOfferCarousel from './dedup/HomepageOfferCarousel'
import { useHomepageHeroOfferPublication } from './dedup/HomepageOfferDedupProvider'

const FAMILY_FRIENDLY_FILTERS = buildFamilyFriendlyOfferFilters(isManualSortingEnabledFor('carousel-hotel'))

interface Props {
  onFamilyFriendlyListEvent: OfferListEventHandler
  onRecentSearchHotelListEvent: OfferListEventHandler
}

function renderItalic(chunks: ReactNode) {
  return <i key="family-title-italic">{chunks}</i>
}

// All candidates for the shared slot must feed this selector before their
// treatment is rendered. Never fall through to another candidate in Control.
function HomepageSearchSlotOfferCarousel(props: Props) {
  const { onFamilyFriendlyListEvent, onRecentSearchHotelListEvent } = props
  const intl = useIntl()
  // Experiment availability controls participation; the arm never selects the winner.
  const { variation, isTurnedOn: familyExperimentEnabled, triggerActivation } = useExperiment(
    ChertoExperiments.croHomepageFamilyFriendlyOfferCarousel,
    { canActivate: false, overrideTrigger: true },
  )
  const { isTurnedOn: recentSearchExperimentEnabled } = useExperiment(
    ChertoExperiments.homePageRecentSearchesCarousel,
    { canActivate: false },
  )
  const familyAudience = useFamilySearchIntent(familyExperimentEnabled)
  const { publication, getCurrentPublication } = useHomepageHeroOfferPublication()
  const trackedDecision = useRef<PersonalisedCarouselSelection | undefined>(undefined)
  const recentSearchState = useRecentSearchHotelCarousel(publication.offerIds, recentSearchExperimentEnabled)
  const familyOffers = useOfferList(FAMILY_FRIENDLY_FILTERS, { disabled: !familyExperimentEnabled || !familyAudience.eligible, preferListingOffers: true })

  const familyStatus = useMemo(() => {
    if (!familyExperimentEnabled) return 'ineligible'
    if (familyAudience.pending) return 'loading'
    if (!familyAudience.eligible) return 'ineligible'
    if (!publication.ready || familyOffers.fetching) return 'loading'
    if (familyOffers.error || familyOffers.offerIds.length === 0) return 'ineligible'
    return 'eligible'
  }, [familyExperimentEnabled, familyAudience.pending, familyAudience.eligible, publication.ready, familyOffers.fetching, familyOffers.error, familyOffers.offerIds.length])

  const decision = useMemo(() => {
    let recentSearchStatus: PersonalisedCarouselCandidate['status'] = 'ineligible'
    if (recentSearchState.decision.status === 'eligible') recentSearchStatus = 'eligible'
    if (recentSearchState.decision.status === 'loading') recentSearchStatus = 'loading'

    return selectPersonalisedCarousel([
      { id: 'family-friendly', status: familyStatus },
      { id: 'post-purchase-hotels', status: recentSearchStatus },
      // CRO-7353 must register audience + inventory here when implemented.
      { id: 'weekend-stays', status: 'ineligible' },
    ])
  }, [familyStatus, recentSearchState.decision.status])

  const familySelected = decision.selected === 'family-friendly'

  useEffect(() => {
    // Hero inventory can change during this commit. Wait for the current
    // publication before exposing either experiment or recording its selection.
    if (!publication.ready || publication !== getCurrentPublication()) return
    if (decision.reason === 'eligibility_pending') return

    if (familySelected) triggerActivation()

    if (trackedDecision.current === decision) return
    trackedDecision.current = decision
    trackClientEvent(buildSearchSlotSelectionEvent(decision))
  }, [decision, familySelected, getCurrentPublication, publication, triggerActivation])

  const title = intl.formatMessage(
    {
      id: 'q3XBWtkRnnwtEn4zdu34',
      defaultMessage: 'Our top picks for <i>family</i> escapes',
      description: 'Draft homepage carousel heading with family italicised for customers with a recent submitted search including children',
    },
    { i: renderItalic },
  )

  return <>
    {familySelected && variation === 'variant' && <HomepageOfferCarousel
      id="familyFriendlyOfferCarousel"
      mode="allow"
      reason="Family-friendly discovery retains its holiday-type inventory independently of other rows, keeping inventory eligibility identical in Control and Variant."
    >
      <OfferListEventsProvider onListEvent={onFamilyFriendlyListEvent}>
        <OfferCardCarouselSection
          title={title}
          filters={FAMILY_FRIENDLY_FILTERS}
          overrideOfferList={familyOffers}
          preferListingOffers
          allLink="/search?holidayTypes=Family%20Friendly"
          hideWhenEmpty
          tileStyle="card-default"
          tileSize="medium"
          data-testid="homepage-family-friendly-carousel"
        />
      </OfferListEventsProvider>
    </HomepageOfferCarousel>}
    <RecentSearchHotelOfferCarousel
      onListEvent={onRecentSearchHotelListEvent}
      carouselState={recentSearchState}
      selected={decision.selected === 'post-purchase-hotels'}
    />
  </>
}

export default HomepageSearchSlotOfferCarousel
